import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Database, Settings, AlertCircle, RefreshCw, Cloud, Globe } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DocumentUploadZone } from '@/components/knowledge-bases/document-upload-zone'
import { DocumentList } from '@/components/knowledge-bases/document-list'
import { GoogleDriveSyncConfigDialog } from '@/components/knowledge-bases/google-drive-sync-config-dialog'
import { NotionSyncConfigDialog } from '@/components/knowledge-bases/notion-sync-config-dialog'
import { UrlSyncConfigDialog } from '@/components/knowledge-bases/url-sync-config-dialog'
import { SyncHistory } from '@/components/knowledge-bases/sync-history'
import { knowledgeBaseService } from '@/services/knowledge-base'
import { documentService } from '@/services/document'
import { googleIntegrationService } from '@/services/google-integration'
import { notionIntegrationService } from '@/services/notion-integration'
import { useOrganizationStore } from '@/stores/organization-store'
import type { KnowledgeBase } from '@/schemas/knowledge-base'
import type { DocumentListResponse, DocumentStatus, DocumentUploadResponse } from '@/schemas/document'
import type { GoogleConnection } from '@/schemas/google-integration'
import type { NotionConnection } from '@/schemas/notion-integration'

// Polling interval for document status updates (5 seconds)
const POLLING_INTERVAL = 5000

export function KnowledgeBaseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation('knowledge')
  const { currentProject, currentOrganization } = useOrganizationStore()

  // State
  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBase | null>(null)
  const [documents, setDocuments] = useState<DocumentListResponse | null>(null)
  const [isLoadingKB, setIsLoadingKB] = useState(true)
  const [isLoadingDocs, setIsLoadingDocs] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Google Drive sync state
  const [syncDialogOpen, setSyncDialogOpen] = useState(false)
  const [googleConnection, setGoogleConnection] = useState<GoogleConnection | null>(null)

  // Notion sync state
  const [notionSyncDialogOpen, setNotionSyncDialogOpen] = useState(false)
  const [notionConnection, setNotionConnection] = useState<NotionConnection | null>(null)

  // URL sync state
  const [urlSyncDialogOpen, setUrlSyncDialogOpen] = useState(false)

  // Filters
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | undefined>()
  const [search, setSearch] = useState('')

  // Polling ref
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Fetch Google connection status
  const fetchGoogleStatus = useCallback(async () => {
    if (!currentOrganization?.id) return

    try {
      const status = await googleIntegrationService.getStatus(currentOrganization.id)
      setGoogleConnection(status.connected ? status.connection : null)
    } catch (err) {
      console.error('Error fetching Google status:', err)
      // Not an error to show to user - Google might not be connected
    }
  }, [currentOrganization?.id])

  // Fetch Notion connection status
  const fetchNotionStatus = useCallback(async () => {
    if (!currentOrganization?.id) return

    try {
      const status = await notionIntegrationService.getStatus(currentOrganization.id)
      setNotionConnection(status.connected ? status.connection : null)
    } catch (err) {
      console.error('Error fetching Notion status:', err)
      // Not an error to show to user - Notion might not be connected
    }
  }, [currentOrganization?.id])

  // Fetch knowledge base
  const fetchKnowledgeBase = useCallback(async () => {
    if (!currentProject?.id || !id) return

    try {
      const kb = await knowledgeBaseService.getKnowledgeBase(currentProject.id, id)
      setKnowledgeBase(kb)
      setError(null)
    } catch (err) {
      setError(t('detail.loadError'))
      console.error('Error fetching knowledge base:', err)
    } finally {
      setIsLoadingKB(false)
    }
  }, [currentProject?.id, id, t])

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    if (!currentProject?.id || !id) return

    try {
      const docs = await documentService.listDocuments(currentProject.id, id, {
        page,
        page_size: 20,
        status: statusFilter,
        search: search || undefined,
      })
      setDocuments(docs)
    } catch (err) {
      setError(t('detail.documentsLoadError'))
      console.error('Error fetching documents:', err)
    } finally {
      setIsLoadingDocs(false)
    }
  }, [currentProject?.id, id, page, statusFilter, search, t])

  // Check if we need to poll for status updates
  const shouldPoll = useCallback(() => {
    if (!documents) return false
    return documents.documents.some(
      (doc) => doc.status === 'pending' || doc.status === 'processing'
    )
  }, [documents])

  // Setup polling for document status
  useEffect(() => {
    // Clear existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }

    // Start polling if needed
    if (shouldPoll()) {
      pollingRef.current = setInterval(() => {
        fetchDocuments()
        fetchKnowledgeBase() // Update document count
      }, POLLING_INTERVAL)
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [shouldPoll, fetchDocuments, fetchKnowledgeBase])

  // Initial load
  useEffect(() => {
    fetchKnowledgeBase()
    fetchDocuments()
    fetchGoogleStatus()
    fetchNotionStatus()
  }, [fetchKnowledgeBase, fetchDocuments, fetchGoogleStatus, fetchNotionStatus])

  // Handle upload complete
  const handleUploadComplete = (response: DocumentUploadResponse) => {
    if (response.uploaded_count > 0) {
      setSuccessMessage(
        t('detail.uploadSuccess', { count: response.uploaded_count })
      )
      setTimeout(() => setSuccessMessage(null), 5000)
    }
    // Refresh documents and KB
    fetchDocuments()
    fetchKnowledgeBase()
  }

  // Handle upload error
  const handleUploadError = (error: Error) => {
    setError(error.message)
    setTimeout(() => setError(null), 5000)
  }

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    setIsLoadingDocs(true)
  }

  // Handle status filter
  const handleStatusFilter = (status: DocumentStatus | undefined) => {
    setStatusFilter(status)
    setPage(1)
    setIsLoadingDocs(true)
  }

  // Handle search
  const handleSearch = (searchTerm: string) => {
    setSearch(searchTerm)
    setPage(1)
    setIsLoadingDocs(true)
  }

  // Handle refresh
  const handleRefresh = () => {
    setIsLoadingDocs(true)
    fetchDocuments()
    fetchKnowledgeBase()
  }

  // No project selected
  if (!currentProject) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-20 h-20 rounded-2xl bg-amber-500/20 flex items-center justify-center mb-6">
          <Database className="w-10 h-10 text-amber-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-200 mb-2">
          {t('detail.noProjectSelected')}
        </h3>
        <p className="text-gray-500 text-center max-w-md">
          {t('detail.noProjectSelectedDesc')}
        </p>
      </div>
    )
  }

  // Loading state
  if (isLoadingKB && !knowledgeBase) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="animate-pulse">
          <div className="h-6 bg-gray-700/50 rounded w-32 mb-4" />
          <Card variant="glass" className="p-6">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-xl bg-gray-700/50" />
              <div className="flex-1 space-y-2">
                <div className="h-6 bg-gray-700/50 rounded w-1/3" />
                <div className="h-4 bg-gray-700/50 rounded w-1/2" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  // Not found
  if (!knowledgeBase && !isLoadingKB) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-20 h-20 rounded-2xl bg-red-500/20 flex items-center justify-center mb-6">
          <AlertCircle className="w-10 h-10 text-red-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-200 mb-2">
          {t('detail.notFound')}
        </h3>
        <p className="text-gray-500 text-center max-w-md mb-6">
          {t('detail.notFoundDesc')}
        </p>
        <Button variant="ghost" onClick={() => navigate('/knowledge-base')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('detail.backToList')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/knowledge-base')}
        className="text-gray-400 hover:text-white -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('detail.backToList')}
      </Button>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert className="bg-emerald-500/10 border-emerald-500/50">
          <AlertDescription className="text-emerald-400">{successMessage}</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive" className="bg-red-500/10 border-red-500/50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* KB Header */}
      <Card variant="glass" className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-6">
            {/* Icon */}
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Database className="w-8 h-8 text-emerald-400" />
            </div>

            {/* Info */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-white">{knowledgeBase?.name}</h1>
                <span
                  className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    knowledgeBase?.is_active
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-amber-500/20 text-amber-400'
                  }`}
                >
                  {knowledgeBase?.is_active ? t('status.active') : t('status.inactive')}
                </span>
              </div>
              <p className="text-gray-400 mb-3">
                {knowledgeBase?.description || t('detail.noDescription')}
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>
                  {t('detail.documentCount', { count: knowledgeBase?.document_count || 0 })}
                </span>
                <span className="text-gray-700">|</span>
                <span>
                  {t('detail.chunkConfig', { size: knowledgeBase?.chunking_config?.chunk_size || 1000 })}
                </span>
                <span className="text-gray-700">|</span>
                <span>
                  {t('detail.overlapConfig', { overlap: knowledgeBase?.chunking_config?.chunk_overlap || 200 })}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            {googleConnection && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSyncDialogOpen(true)}
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
              >
                <Cloud className="w-4 h-4 mr-2" />
                {t('sync.googleDrive')}
              </Button>
            )}
            {notionConnection && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNotionSyncDialogOpen(true)}
                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.98-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.373.466l1.822 1.447zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.84-.046.933-.56.933-1.167V6.354c0-.606-.233-.933-.746-.886l-15.177.887c-.56.046-.747.326-.747.933zm14.337.746c.093.42 0 .84-.42.887l-.7.14v10.264c-.607.327-1.167.514-1.634.514-.746 0-.933-.234-1.493-.934l-4.577-7.186v6.952l1.446.327s0 .84-1.167.84l-3.22.187c-.093-.187 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.213-.14c-.094-.513.28-.886.746-.933l3.221-.186zM2.17 1.4L15.984.14c1.682-.14 2.102.094 2.802.607l3.876 2.707c.467.326.607.7.607 1.167v16.756c0 1.027-.373 1.634-1.68 1.727l-15.458.933c-.98.047-1.447-.093-1.96-.7l-3.127-4.057c-.56-.746-.793-1.306-.793-1.96V2.942c0-.839.374-1.54 1.727-1.633l.193.093z" />
                </svg>
                {t('sync.notion')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUrlSyncDialogOpen(true)}
              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
            >
              <Globe className="w-4 h-4 mr-2" />
              {t('sync.url')}
            </Button>
            <Button variant="ghost" size="sm" disabled className="text-gray-400">
              <Settings className="w-4 h-4 mr-2" />
              {t('settings.configure')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Upload Zone */}
      {knowledgeBase?.is_active && (
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">{t('detail.uploadDocuments')}</h2>
          <DocumentUploadZone
            projectId={currentProject.id}
            kbId={id!}
            onUploadComplete={handleUploadComplete}
            onError={handleUploadError}
          />
        </div>
      )}

      {!knowledgeBase?.is_active && (
        <Alert className="bg-amber-500/10 border-amber-500/50">
          <AlertCircle className="h-4 w-4 text-amber-400" />
          <AlertDescription className="text-amber-400">
            {t('detail.inactiveWarning')}
          </AlertDescription>
        </Alert>
      )}

      {/* Documents List */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">{t('detail.documents')}</h2>
        <DocumentList
          projectId={currentProject.id}
          kbId={id!}
          documents={documents}
          isLoading={isLoadingDocs}
          onRefresh={handleRefresh}
          onPageChange={handlePageChange}
          onStatusFilter={handleStatusFilter}
          onSearch={handleSearch}
        />
      </div>

      {/* Sync History */}
      <SyncHistory projectId={currentProject.id} kbId={id!} />

      {/* Google Drive Sync Dialog */}
      {currentOrganization && knowledgeBase && (
        <GoogleDriveSyncConfigDialog
          open={syncDialogOpen}
          onOpenChange={setSyncDialogOpen}
          organizationId={currentOrganization.id}
          projectId={currentProject.id}
          knowledgeBaseId={knowledgeBase.id}
          knowledgeBaseName={knowledgeBase.name}
          googleEmail={googleConnection?.google_email}
          onSyncConfigured={() => {
            setSuccessMessage(t('detail.syncConfigSaved'))
            setTimeout(() => setSuccessMessage(null), 5000)
          }}
          onError={(msg) => {
            setError(msg)
            setTimeout(() => setError(null), 5000)
          }}
        />
      )}

      {/* Notion Sync Dialog */}
      {currentOrganization && knowledgeBase && (
        <NotionSyncConfigDialog
          open={notionSyncDialogOpen}
          onOpenChange={setNotionSyncDialogOpen}
          organizationId={currentOrganization.id}
          projectId={currentProject.id}
          knowledgeBaseId={knowledgeBase.id}
          knowledgeBaseName={knowledgeBase.name}
          workspaceName={notionConnection?.workspace_name}
          onSyncConfigured={() => {
            setSuccessMessage(t('detail.syncConfigSaved'))
            setTimeout(() => setSuccessMessage(null), 5000)
          }}
          onError={(msg) => {
            setError(msg)
            setTimeout(() => setError(null), 5000)
          }}
        />
      )}

      {/* URL Sync Dialog */}
      {knowledgeBase && (
        <UrlSyncConfigDialog
          open={urlSyncDialogOpen}
          onOpenChange={setUrlSyncDialogOpen}
          projectId={currentProject.id}
          knowledgeBaseId={knowledgeBase.id}
          knowledgeBaseName={knowledgeBase.name}
          onSyncConfigured={() => {
            setSuccessMessage(t('detail.syncConfigSaved'))
            setTimeout(() => setSuccessMessage(null), 5000)
          }}
          onError={(msg) => {
            setError(msg)
            setTimeout(() => setError(null), 5000)
          }}
        />
      )}
    </div>
  )
}
